/**
 * @documental
 * @export Checkbox
 */

import { forwardRef } from "react";
import { withSubComponents } from "../../utilities/withSubComponents";
import { Field } from "./Field";
import { Input } from "./Input";
import { RefComponent } from "../RefComponent";
import { InputBaseProps } from "../InputBaseProps";
import { Placeholder } from "./Placeholder";
import { NoPaddingFormComponents } from "../shared";
import { AccessibilityProps } from "../../AccessibilityProps";

/** Values accepted by the `Checkbox` component. */
export type CheckboxValue = boolean | null | "indeterminate";

/**
 * The `Checkbox` component properties.
 */

export interface CheckboxProps extends InputBaseProps<CheckboxValue>,
  NoPaddingFormComponents, Pick<AccessibilityProps, "aria-label"> {}

/**
 * The Checkbox component sub components.
 */
export interface CheckboxSubComponents {
  /**
   * A React Hook Form compatible Checkbox component. For more information, see the documentation for
   * the <a href="https://react-hook-form.com/api">React Hook Form API</a>.
   */
  readonly Field: typeof Field;

  /**
   * A placeholder component for the Radio which can be displayed while loading.
   */
  readonly Placeholder: typeof Placeholder;
}

/**
 * A checkbox component.
 *
 * `import { Checkbox } from "capitalrx-components-2.0/lib/forms";`
 *
 * <strong>To use the React Hook Form version add the following use the `Field` sub component:</strong>
 *
 * `<Checkbox.Field .../>`
 *
 * <strong>To use the regular version of the TextArea</strong>
 *
 * `<Checkbox .../>`
 *
 * The regular version acts like a normal field, in order to make it work
 * you need to pass in a `value` and an `onChange` for it to be interactive
 *
 * ```typescript
  const [value, onChange] = useSimpleInputState<boolean | null | "indeterminate">(false);
  return <Checkbox label="Checkbox" onChange={(newValue, prev) => onChange(newValue, prev)} value={value} />;
  ```
 */

export const Checkbox = withSubComponents<RefComponent<HTMLInputElement, CheckboxProps>, CheckboxSubComponents>(
  {
    Field,
    Placeholder,
  },
  forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(props, ref) {
    return <Input {...props} ref={ref} />;
  }),
);
